import styled from 'styled-components';

interface SidebarContainerProps {
  $collapsed?: boolean;
}

/**
 * Largura da barra, em UM lugar só.
 *
 * O `CollapseButton` se posiciona a partir dela (ele fica centrado sobre a borda direita, fora do
 * container). Com a medida escrita nos dois lugares, o primeiro ajuste de largura deixa o botão
 * flutuando longe da borda — e nada acusa, porque não há teste de layout. Uma constante torna a
 * divergência impossível.
 */
export const LARGURA_RECOLHIDA = 80;
export const LARGURA_EXPANDIDA = 260;

/** Metade do botão de 32px: é o quanto ele avança para fora da borda. */
const AVANCO_DO_BOTAO = 16;

/** Curva usada pela largura da barra. O botão reusa a MESMA, senão eles se descolam na animação. */
const CURVA_DA_BARRA = 'cubic-bezier(0.25, 0.8, 0.25, 1)';

export const SidebarContainer = styled.div<SidebarContainerProps>`
  width: ${props => props.$collapsed ? `${LARGURA_RECOLHIDA}px` : `${LARGURA_EXPANDIDA}px`};
  position: fixed;
  height: calc(100vh - 80px);
  top: 80px;
  left: 0;
  background: ${({ theme }) => theme.sidebar}e6; /* Slight transparency */
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: ${props => props.$collapsed ? '24px 10px' : '32px 16px'};
  display: flex;
  flex-direction: column;
  gap: ${props => props.$collapsed ? '6px' : '8px'};
  border-right: 1px solid ${({ theme }) => theme.border};
  transition: width 0.3s cubic-bezier(0.25, 0.8, 0.25, 1),
    padding 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 999;
  will-change: width, padding;
  
  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.border};
    border-radius: 3px;
  }

  /* Responsividade para tablets */
  @media (max-width: 1024px) {
    width: 80px;
    padding: 24px 8px;
  }

  /* Responsividade para mobile */
  @media (max-width: 768px) {
    position: fixed;
    top: 64px;
    left: 0;
    width: 280px;
    height: calc(100vh - 64px);
    margin: 0;
    padding: 20px 16px;
    border-right: none;
    transform: translateX(${props => props.$collapsed ? '-100%' : '0'});
    box-shadow: ${props => props.$collapsed ? 'none' : '4px 0 24px rgba(0, 0, 0, 0.1)'};
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    will-change: transform;
  }
`;

/**
 * Botão de recolher/expandir. Renderizado como IRMÃO do `SidebarContainer`, não filho.
 *
 * Ele era `position: absolute; right: -16px` dentro do container — metade dele para fora da borda,
 * que é o efeito visual pretendido. Mas o container tem `overflow-x: hidden` (necessário: sem ele o
 * rótulo longo gera rolagem horizontal na barra recolhida), e overflow diferente de `visible`
 * RECORTA o que passa da borda. A metade de fora simplesmente não era desenhada.
 *
 * Como irmão, ele se posiciona pela viewport. O container é `position: fixed; left: 0`, então
 * `left = largura - metade do botão` reproduz exatamente o lugar pretendido, sem nada recortando e
 * sem mexer no `overflow` do container.
 */
export const CollapseButton = styled.button<{ $collapsed?: boolean }>`
  position: fixed;
  /* Alinha com o topo do conteúdo da barra: 80px do header + 24px do padding interno. */
  top: ${80 + 24}px;
  left: ${props =>
    (props.$collapsed ? LARGURA_RECOLHIDA : LARGURA_EXPANDIDA) - AVANCO_DO_BOTAO}px;
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.borderLight || theme.border};
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  /*
    A propriedade left usa a curva DA BARRA, não a de overshoot.
    A curva bouncy (0.175, 0.885, 0.32, 1.275) passa de 1 e volta — ótimo para o scale do hover,
    péssimo para left: o botão avançaria além da borda e voltaria, descolando da barra no meio da
    animação. Só o hover fica com ela.

    (Sem crase neste comentário de propósito: crase dentro de comentário CSS ENCERRA o template
    literal do styled-components, e o erro aparece como TS1005 numa linha visivelmente correta.)
  */
  transition: left 0.3s ${CURVA_DA_BARRA},
    background 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);

  &:hover {
    background: linear-gradient(135deg, ${({ theme }) => theme.primary}, ${({ theme }) => theme.primaryHover || theme.primary});
    color: ${({ theme }) => theme.primaryOn};
    border-color: transparent;
    transform: scale(1.15);
    box-shadow: 0 6px 16px ${({ theme }) => theme.primary}50;
  }

  /* Hide on mobile/tablet or adjust */
  @media (max-width: 1024px) {
    display: none;
  }
`;

interface SidebarItemProps {
  $active?: boolean;
  $collapsed?: boolean;
}

export const SidebarItem = styled.div<SidebarItemProps>`
  display: flex;
  align-items: center;
  padding: ${props => props.$collapsed ? '12px 12px' : '12px 16px'};
  cursor: pointer;
  background-color: ${props => props.$active ? props.theme.sidebarItemActive : 'transparent'};
  color: ${props => props.$active ? props.theme.primaryAccent : props.theme.textSidebar};
  border-radius: 12px;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  justify-content: ${props => props.$collapsed ? 'center' : 'flex-start'};
  position: relative;
  min-height: 48px;
  font-weight: ${props => props.$active ? '600' : '500'};
  border: 1px solid ${props => props.$active ? props.theme.borderLight + '30' : 'transparent'};
  
  span {
    color: ${props => props.$active ? props.theme.text : props.theme.textSidebar};
    transition: color 0.2s ease;
  }
  
  &:hover {
    background-color: ${props => props.$active ? props.theme.sidebarItemActive : props.theme.sidebarItemHover};
    color: ${props => props.$active ? props.theme.primaryAccent : props.theme.text};
    transform: translateX(4px);
    
    span {
      color: ${({ theme }) => theme.text};
    }
  }

  /* Tooltip para estado colapsado */
  ${props => props.$collapsed && `
    &:hover::after {
      content: attr(data-tooltip);
      position: absolute;
      left: 100%;
      top: 50%;
      transform: translateY(-50%);
      margin-left: 10px;
      background-color: ${props.theme.tooltip};
      color: ${props.theme.text};
      border: 1px solid ${props.theme.tooltipBorder};
      padding: 6px 10px;
      border-radius: 6px;
      font-size: 13px;
      white-space: nowrap;
      z-index: 1000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      pointer-events: none;
    }
  `}

  @media (max-width: 1024px) {
    padding: 12px;
    justify-content: center;
  }
`;

export const SidebarIcon = styled.div<{ $collapsed?: boolean }>`
  font-size: 20px;
  margin-right: ${props => props.$collapsed ? '0' : '12px'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: margin-right 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  flex-shrink: 0;

  @media (max-width: 1024px) {
    margin-right: 0;
  }
`;

export const SidebarText = styled.span<{ $collapsed?: boolean }>`
  font-size: 14px;
  white-space: nowrap;
  opacity: ${props => props.$collapsed ? 0 : 1};
  max-width: ${props => props.$collapsed ? 0 : '200px'};
  transform: translateX(${props => props.$collapsed ? '-6px' : '0'});
  transition: max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: ${props => props.$collapsed ? 'none' : 'auto'};
  
  @media (max-width: 1024px) {
    display: none;
  }

  @media (max-width: 768px) {
    display: block;
    max-width: none;
    opacity: 1;
  }
`;

// Overlay para mobile
export const SidebarOverlay = styled.div<{ $visible: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: ${props => props.$visible ? 'block' : 'none'};
    position: fixed;
    inset: 0;
    top: 64px;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 998;
    backdrop-filter: blur(4px);
    opacity: ${props => props.$visible ? '1' : '0'};
    transition: opacity 0.3s ease;
  }
`;
